import type { GenerationKind, GenerationSummary } from '../model/types';
import { isActiveSummary, sameSummary } from './cache';
import { SLOW_AFTER_MS } from './statusView';

/**
 * Слежение за нажатиями «Создать» на главной (ТЗ главной § 8, «Данные»; § 23.4) — чистая модель без
 * alias-импортов; время приходит аргументом. Здесь заходы на главную, запасная сводка из ответа
 * «Создать» (пока записи нет в кэше списка), карточки нажатия (тосты по ним главная не показывает),
 * `announced` и `dismissed`; то, что пишет список «Недавно добавленных»: когда строка записи появилась
 * (`insertedAt`), какие строки на экране (`inView`, с задержкой 150 мс у границы) и запрос раскрыть
 * запись (`revealRequest`); то, что пишет строка создания: открыты ли слои (`overlayOpen`) и клавиатура
 * (`keyboardOpen`). Решения сигнала у строки — тоже здесь: что показать (`computeSignal`), когда итог
 * принят (`stepSignal`), что объявить экранному диктору (`pendingSpeech`). Только в памяти: после
 * перезагрузки слежения нет, итог виден в списке.
 */

/** Что уже объявила скрытая live-область строки: начало и итог — по одному разу (ТЗ § 8). */
export type SpokenPhase = 'none' | 'started' | 'final';

export interface WatchEntry {
  id: string;
  /** Сводка из ответа «Создать»; её же обновляет upsertGenerationSummary. */
  summary: GenerationSummary | null;
  cardIds: string[];
  /** Номер захода на главную, в котором было нажатие. */
  visit: number;
  /** Когда нажали «Создать» или «Повторить» — отсчёт «дольше обычного» (ТЗ § 22.4). */
  startedAt: number;
  /** Когда строка записи доехала в списке после нажатия (въезд по высоте закончился): сигнал решает не раньше
   *  чем через 300 мс (ТЗ § 8). У «Повторить» и когда записи некуда встать — момент ответа. */
  insertedAt: number | null;
  /** Строка записи уже в списке (в DOM), но ещё не доехала. Пока она въезжает, запасной срок от ответа (1,5 с)
   *  не действует: при редких кадрах (свёрнутое окно, медленное устройство) строка доезжает позже, и сигнал
   *  решал бы по строке высотой 0 (приёмка A2-01). Не доехала за 5 с — решаем без неё. */
  mountedAt: number | null;
  /** Решение о сигнале принято: дальше слои делают сигнал прозрачным, а не «ещё не решённым». */
  decidedAt: number | null;
  /** С какого момента строка законченной записи на экране при соблюдённых условиях (принятие, § 8). */
  seenSince: number | null;
  /** Итог принят — сигнала по записи больше нет. */
  seenFinal: boolean;
  /** Сколько «Готово» по записи уже было на виду до нынешнего показа (мс). */
  readyShownMs: number;
  /** С какого момента «Готово» по записи на виду сейчас (отсчёт 6 с идёт). */
  readyShownSince: number | null;
  /** Нажали сигнал, пока шла генерация: пока едет прокрутка к записи, сигнал по ней не показываем. */
  revealedAt: number | null;
  /** Тост о его карточках всё же показан (ушёл с главной до конца). */
  announced: boolean;
  /** Ученик убрал итог крестиком. */
  dismissed: boolean;
  /** Что о записи уже сказала live-область. */
  spoken: SpokenPhase;
}

/** Строка записи в списке: на экране ли она (над полосой видно ≥ 60 %). */
export interface RowView {
  /** Последнее наблюдение. */
  visible: boolean;
  /** Когда оно пришло. */
  at: number;
  /** Значение, которое продержалось 150 мс к моменту последнего наблюдения; `null` — устоявшегося
   *  значения ещё не было (строка только появилась). */
  settled: boolean | null;
}

/** Нажатие сигнала: раскрыть запись и прокрутить к ней. */
export interface RevealRequest {
  id: string;
  at: number;
}

export interface WatchState {
  /** Главная открыта: тосты о карточках отслеживаемых генераций не показываются. */
  homeVisible: boolean;
  /** Номер текущего захода на главную — растёт при каждом входе. */
  visit: number;
  entries: Record<string, WatchEntry>;
  /** Строки записей на экране — пишет список (IntersectionObserver с корнем main). */
  inView: Record<string, RowView>;
  /** Нажатие сигнала: список расширяет окно, раскрывает запись, прокручивает к ней и вызывает consumeReveal(). */
  revealRequest: RevealRequest | null;
  /** Открыты лист, превью, этап 2 или меню модулей — пишет строка создания. */
  overlayOpen: boolean;
  /** С какого момента слои считаются закрытыми (на телефоне — через 120 мс после ухода этапа 2, § 13). */
  overlayFreeAt: number;
  /** Клавиатура открыта (фокус в поле на сенсорном устройстве) — пишет строка создания. */
  keyboardOpen: boolean;
}

export const initialWatchState: WatchState = {
  homeVisible: false,
  visit: 0,
  entries: {},
  inView: {},
  revealRequest: null,
  overlayOpen: false,
  overlayFreeAt: 0,
  keyboardOpen: false,
};

/** Новое значение «строка на экране» действует, когда продержалось столько (ТЗ § 8): у границы
 *  при медленной прокрутке сигнал не дёргается. */
export const VIEW_SETTLE_MS = 150;
/** Решение о сигнале — не раньше чем через 300 мс после того, как строка записи доехала (ТЗ § 8): наблюдение
 *  «на экране» по её последнему кадру въезда успевает устояться (150 мс). Отсчёт от появления строки в DOM
 *  при редких кадрах кончался раньше, чем строка получала высоту (приёмка A2-01). */
export const SIGNAL_DECIDE_MS = 300;
/** Строка записи так и не появилась (список не загрузился, запись не встала в окно) — решаем от ответа. */
export const SIGNAL_ROW_WAIT_MS = 1500;
/** Строка в списке, но не доехала (кадров нет: свёрнутое окно, медленное устройство) — дольше не ждём. */
export const SIGNAL_ARRIVE_WAIT_MS = 5000;
/** Итог принят, если строка законченной записи была на экране столько подряд (ТЗ § 8). */
export const ACCEPT_IN_VIEW_MS = 1000;
/** «Готово» уходит, когда пробыло на виду столько (ТЗ § 8). */
export const READY_SHOWN_MS = 6000;
/** Нажали сигнал идущей генерации: сигнал по ней не показываем, пока едет прокрутка к записи. */
export const REVEAL_HOLD_MS = 1000;

/** Вход на главную — новый заход: всё, что отслеживалось раньше, теперь прошлый заход. */
export function enterHome(state: WatchState): WatchState {
  return {
    ...state,
    homeVisible: true,
    visit: state.visit + 1,
    inView: {},
    revealRequest: null,
    overlayOpen: false,
    overlayFreeAt: 0,
    keyboardOpen: false,
  };
}

export function leaveHome(state: WatchState): WatchState {
  return { ...state, homeVisible: false, inView: {}, revealRequest: null, overlayOpen: false, overlayFreeAt: 0, keyboardOpen: false };
}

export interface WatchOptions {
  /**
   * «Повторить» в записи — новая попытка в той же записи (ТЗ § 23.4, п. 2): отсчёт «дольше обычного»
   * заново, крестик, тост, принятие и объявления сбрасываются, а решение о сигнале — от ответа: вставки
   * нет, строка записи там, где нажали.
   */
  restart?: boolean;
  /** Записи сейчас некуда встать (список не загружен): сигнал решает от ответа, а не от вставки строки. */
  noRow?: boolean;
}

/** Успешное «Создать», «Добавить» или «Повторить» этого захода. Повтор того же id (ответ `replayed`)
 *  дописывает карточки и сводку, а не начинает отсчёт заново. */
export function watchGeneration(
  state: WatchState,
  summary: GenerationSummary,
  cardIds: readonly string[],
  now: number,
  options: WatchOptions = {},
): WatchState {
  const previous = state.entries[summary.id];
  const merged = [...new Set([...(previous?.cardIds ?? []), ...cardIds])];
  let entry: WatchEntry;
  if (previous && !options.restart) {
    entry = { ...previous, summary, cardIds: merged, visit: state.visit };
  } else {
    entry = {
      id: summary.id,
      summary,
      cardIds: merged,
      visit: state.visit,
      startedAt: now,
      insertedAt: options.restart || options.noRow ? now : null,
      mountedAt: null,
      decidedAt: null,
      seenSince: null,
      seenFinal: false,
      readyShownMs: 0,
      readyShownSince: null,
      revealedAt: null,
      announced: false,
      dismissed: false,
      spoken: 'none',
    };
  }
  return { ...state, entries: { ...state.entries, [summary.id]: entry } };
}

/** Свежая сводка (ответ, SSE, опрос) для отслеживаемой записи; чужие записи не заводит. Та же по содержимому
 *  (разборщик каждый раз даёт новый объект) — тот же стор: подписчики не просыпаются на каждое событие. */
export function updateWatchedSummary(state: WatchState, summary: GenerationSummary): WatchState {
  const entry = state.entries[summary.id];
  if (!entry || (entry.summary !== null && sameSummary(entry.summary, summary))) return state;
  return { ...state, entries: { ...state.entries, [summary.id]: { ...entry, summary } } };
}

/** Строки записей доехали в списке: у отслеживаемых, где момент ещё не записан, — `insertedAt`. */
export function markInserted(state: WatchState, ids: readonly string[], now: number): WatchState {
  let entries: Record<string, WatchEntry> | null = null;
  for (const id of ids) {
    const entry = state.entries[id];
    if (!entry || entry.insertedAt !== null) continue;
    entries = entries ?? { ...state.entries };
    entries[id] = { ...entry, insertedAt: now };
  }
  return entries ? { ...state, entries } : state;
}

/** Строки записей появились в списке (ещё могут въезжать): у отслеживаемых, где момент не записан, — `mountedAt`. */
export function markMounted(state: WatchState, ids: readonly string[], now: number): WatchState {
  let entries: Record<string, WatchEntry> | null = null;
  for (const id of ids) {
    const entry = state.entries[id];
    if (!entry || entry.mountedAt !== null || entry.insertedAt !== null) continue;
    entries = entries ?? { ...state.entries };
    entries[id] = { ...entry, mountedAt: now };
  }
  return entries ? { ...state, entries } : state;
}

/** Наблюдение списка: строка записи на экране или нет. Повтор того же значения ничего не меняет. */
export function observeRowView(state: WatchState, id: string, visible: boolean, now: number): WatchState {
  const previous = state.inView[id];
  if (previous && previous.visible === visible) return state;
  // Прежнее значение продержалось 150 мс — теперь оно «устоявшееся», пока новое не продержится столько же.
  const settled = previous ? (now - previous.at >= VIEW_SETTLE_MS ? previous.visible : previous.settled) : null;
  return { ...state, inView: { ...state.inView, [id]: { visible, at: now, settled } } };
}

/** На экране ли строка записи: последнее значение, если продержалось 150 мс, иначе прежнее устоявшееся. */
export function isRowInView(state: WatchState, id: string, now: number): boolean {
  const view = state.inView[id];
  if (!view) return false;
  return now - view.at >= VIEW_SETTLE_MS ? view.visible : (view.settled ?? false);
}

/**
 * Строки записи нет на экране — для сигнала (ТЗ § 8). Строки нет в списке (за пятёркой, список не
 * загружен) — «нет». Строка только появилась и её первое наблюдение ещё не устоялось — «есть»: свежая
 * запись въезжает по высоте, и сигнал не должен мигнуть, пока наблюдатель её догоняет.
 */
export function isRowOutOfView(state: WatchState, id: string, now: number): boolean {
  const view = state.inView[id];
  if (!view) return true;
  if (now - view.at >= VIEW_SETTLE_MS) return !view.visible;
  return view.settled === null ? false : !view.settled;
}

export function requestReveal(state: WatchState, id: string, now: number): WatchState {
  return { ...state, revealRequest: { id, at: now } };
}

/** Список выполнил запрос (или запрос устарел). `at` — какой именно: новый запрос не гасим. */
export function consumeReveal(state: WatchState, at?: number): WatchState {
  if (!state.revealRequest) return state;
  if (at !== undefined && state.revealRequest.at !== at) return state;
  return { ...state, revealRequest: null };
}

/** Тост о карточках всё же показан — помечаем их записи. */
export function markAnnounced(state: WatchState, cardIds: readonly string[]): WatchState {
  const ids = new Set(cardIds);
  let changed = false;
  const entries: Record<string, WatchEntry> = {};
  for (const [key, entry] of Object.entries(state.entries)) {
    if (!entry.announced && entry.cardIds.some((id) => ids.has(id))) {
      entries[key] = { ...entry, announced: true };
      changed = true;
    } else entries[key] = entry;
  }
  return changed ? { ...state, entries } : state;
}

export function dismissWatched(state: WatchState, id: string): WatchState {
  const entry = state.entries[id];
  if (!entry || entry.dismissed) return state;
  return { ...state, entries: { ...state.entries, [id]: { ...entry, dismissed: true } } };
}

/** Записи больше нет (404, модуль удалён) — перестаём следить. */
export function forgetWatched(state: WatchState, id: string): WatchState {
  if (!state.entries[id] && !state.inView[id]) return state;
  const entries = { ...state.entries };
  delete entries[id];
  const inView = { ...state.inView };
  delete inView[id];
  return { ...state, entries, inView };
}

/** Карточка из отслеживаемой генерации — любого захода. */
export function isWatchedCard(state: WatchState, cardId: string): boolean {
  return Object.values(state.entries).some((entry) => entry.cardIds.includes(cardId));
}

/**
 * Тост о карточках не показываем (ТЗ § 8, «Без двойных уведомлений»): главная открыта, и все карточки —
 * из отслеживаемых генераций любого захода. Итог виден в записи и в сигнале у строки; ушёл с главной —
 * тосты как раньше.
 */
export function shouldMuteToast(state: WatchState, cardIds: readonly string[]): boolean {
  return state.homeVisible && cardIds.length > 0 && cardIds.every((id) => isWatchedCard(state, id));
}

/**
 * Среди нажатий этого захода есть идущая генерация — нужен запасной опрос. Прошлые заходы не считаются: их
 * запасная сводка могла застыть на «генерируем» (запись за первой страницей, SSE по записям старше недели
 * сервер не шлёт), и опрос шёл бы раз в 10 с до перезагрузки. Идущие записи списка опрос видит и так.
 */
export function hasActiveWatched(state: WatchState): boolean {
  return Object.values(state.entries).some(
    (entry) => entry.visit === state.visit && entry.summary !== null && isActiveSummary(entry.summary),
  );
}

/** Карточки нажатий, итог которых ученик не принял (ТЗ § 8). */
export interface UnacceptedOutcomes {
  /** Карточки нажатий этого захода с непринятым итогом: сигнал не нажат и не убран, строка законченной записи
   *  не пробыла на экране 1 с, «Готово» не пробыло на виду 6 с, тост ещё не показан. Их сбои показываются. */
  cardIds: string[];
  /** Из них — карточки нажатий, «Готово» которых ни разу не было на виду: их успех тоже показывается. */
  unseenReadyCardIds: string[];
}

/**
 * Уход с главной (ТЗ § 8, «сбой не теряется»): пока главная открыта, тосты о карточках отслеживаемых нажатий
 * не показываются — итог виден в записи и в сигнале. Ушёл, не приняв итог, — подавленные тосты пора показать:
 * сбой — всегда, успех — если «Готово» не появлялось на виду (иначе одно нажатие дало бы два сообщения).
 * Зовётся после `leaveHome`: номер захода ещё тот, из которого ушли. Прошлые заходы молчат, как и решено для
 * возврата на главную: итог показывает запись.
 */
export function unacceptedOutcomes(state: WatchState): UnacceptedOutcomes {
  const cardIds: string[] = [];
  const unseenReadyCardIds: string[] = [];
  for (const entry of Object.values(state.entries)) {
    if (entry.visit !== state.visit || entry.seenFinal || entry.dismissed || entry.announced) continue;
    cardIds.push(...entry.cardIds);
    if (entry.readyShownMs === 0 && entry.readyShownSince === null) unseenReadyCardIds.push(...entry.cardIds);
  }
  return { cardIds, unseenReadyCardIds };
}

/** Нажатия этого захода на главную — под колонкой нужен отступ под сигнал (ТЗ § 5, § 23.4). */
export function hasWatchedInVisit(state: WatchState): boolean {
  return Object.values(state.entries).some((entry) => entry.visit === state.visit);
}

/**
 * Откуда считать «дольше обычного» у записи (ТЗ § 22.4): нажатие этого захода — `startedAt` слежения;
 * иначе — когда этот заход впервые увидел запись в «генерируется» (`firstSeen`).
 */
export function slowSince(state: WatchState, id: string, firstSeen: number | null): number | null {
  const entry = state.entries[id];
  if (entry && entry.visit === state.visit) return entry.startedAt;
  return firstSeen;
}

// --- Сигнал у строки (ТЗ § 8) ---------------------------------------------------------------------

/** Слои закрыты: открыты лист, превью, этап 2 или меню — сигнал прозрачен и клики не ловит. */
export function setOverlay(state: WatchState, open: boolean, now: number, settleMs = 0): WatchState {
  if (open) return state.overlayOpen ? state : { ...state, overlayOpen: true };
  if (!state.overlayOpen) return state;
  return { ...state, overlayOpen: false, overlayFreeAt: now + settleMs };
}

export function setKeyboard(state: WatchState, open: boolean): WatchState {
  return state.keyboardOpen === open ? state : { ...state, keyboardOpen: open };
}

/** Сводка записи: из кэша списка, а пока записи там нет — запасная из слежения. */
export type SummaryOf = (id: string) => GenerationSummary | null;

export const ownSummaryOf =
  (state: WatchState): SummaryOf =>
  (id) =>
    state.entries[id]?.summary ?? null;

/** То, чего нет в сторе: видна ли вкладка, на сигнале ли курсор или фокус, есть ли сигналу место. */
export interface SignalInput {
  /** `document.visibilityState` — вкладка видна. */
  tabVisible: boolean;
  /** Курсор или фокус на сигнале — отсчёт «Готово» стоит. */
  held: boolean;
  /** Сигналу нет места: над строкой облачко, идёт подсветка, строка заблокирована. */
  blocked: boolean;
}

/** Состояние сигнала: «Генерируем», «Дольше обычного:», «Готово:», «Добавлено:» (ручная), «Не получилось:». */
export type SignalState = 'generating' | 'slow' | 'ready' | 'added' | 'failed';

/**
 * Что показать в сигнале у строки — все части текста; какие из них видны на узком окне, решает CSS
 * (ТЗ § 8, «Состав по ширине»).
 */
export interface SignalView {
  /** Запись сигнала: её называет сигнал и к ней ведёт нажатие. */
  id: string;
  state: SignalState;
  kind: GenerationKind;
  /** Слово (строчными) или тема подборки. */
  title: string;
  /** «· N карточек» — только при одной генерации; до 480 px его прячет CSS. */
  cards: number | null;
  /** «· 1 из 2» — только у одной частично неудачной; до 640 px его прячет CSS. */
  failedOf: { failed: number; total: number } | null;
  /** «+N» — сколько ещё генераций в сигнале. */
  more: number;
  /** Сколько генераций в сигнале всего — для события аналитики. */
  count: number;
  /** ✕ «Убрать» — только у «Не получилось»; до 390 px его прячет CSS. */
  dismissible: boolean;
  /** Сигнал есть, но сейчас прозрачен и клики не ловит: слои, клавиатура, облачко, подсветка. */
  suppressed: boolean;
}

const isFinished = (summary: GenerationSummary) => !isActiveSummary(summary) && summary.status !== 'removed';
const isFailedSummary = (summary: GenerationSummary) =>
  !isActiveSummary(summary) && (summary.status === 'partial' || summary.status === 'failed');
const overlayReady = (state: WatchState, now: number) => !state.overlayOpen && now >= state.overlayFreeAt;

/**
 * Решено ли показывать сигнал по записи (ТЗ § 8, «Когда решать после нажатия»): через 300 мс после того, как
 * строка доехала; строка в списке, но ещё въезжает — ждём её до 5 с; строки нет (вставлять некуда) — через 1,5 с
 * после ответа; и не раньше, чем закрылись лист или этап 2. Однажды решённое остаётся решённым — дальше слои
 * только делают сигнал прозрачным.
 */
export function isSignalDecided(state: WatchState, entry: WatchEntry, now: number): boolean {
  if (entry.decidedAt !== null) return true;
  const gate =
    entry.insertedAt !== null
      ? entry.insertedAt + SIGNAL_DECIDE_MS
      : entry.mountedAt !== null
        ? entry.mountedAt + SIGNAL_ARRIVE_WAIT_MS
        : entry.startedAt + SIGNAL_ROW_WAIT_MS;
  return now >= gate && overlayReady(state, now);
}

interface SignalCandidate {
  entry: WatchEntry;
  summary: GenerationSummary;
}

/** Записи сигнала: нажатия этого захода, итог не принят, решение принято, строки нет на экране. Свежие — первыми. */
function signalCandidates(state: WatchState, summaryOf: SummaryOf, now: number): SignalCandidate[] {
  if (!state.homeVisible) return [];
  const out: SignalCandidate[] = [];
  for (const entry of Object.values(state.entries)) {
    if (entry.visit !== state.visit || entry.seenFinal || entry.dismissed) continue;
    const summary = summaryOf(entry.id);
    // Все карточки удалены, пока шла генерация, — сигнал уходит сразу.
    if (!summary || summary.status === 'removed') continue;
    if (!isSignalDecided(state, entry, now)) continue;
    if (entry.revealedAt !== null && now - entry.revealedAt < REVEAL_HOLD_MS) continue;
    if (!isRowOutOfView(state, entry.id, now)) continue;
    out.push({ entry, summary });
  }
  return out.sort((a, b) => b.entry.startedAt - a.entry.startedAt);
}

/** Запись сигнала: хоть одна «не получилось», и больше ничего не идёт, — самая свежая неудачная; иначе — самая свежая. */
function signalRecord(candidates: SignalCandidate[]): SignalCandidate | null {
  if (candidates.length === 0) return null;
  const going = candidates.some((c) => isActiveSummary(c.summary));
  const failed = candidates.find((c) => isFailedSummary(c.summary));
  return failed && !going ? failed : candidates[0];
}

/** Сигнал у строки: что в нём сейчас (или `null` — сигнала нет). */
export function computeSignal(state: WatchState, summaryOf: SummaryOf, input: SignalInput, now: number): SignalView | null {
  const candidates = signalCandidates(state, summaryOf, now);
  const record = signalRecord(candidates);
  if (!record) return null;
  const { entry, summary } = record;
  const single = candidates.length === 1;
  const { counts } = summary;
  let signalState: SignalState;
  if (isActiveSummary(summary)) signalState = now - entry.startedAt >= SLOW_AFTER_MS ? 'slow' : 'generating';
  else if (summary.status === 'ready') signalState = summary.kind === 'manual' ? 'added' : 'ready';
  else signalState = 'failed';
  // Хвост «· 1 из 2» — как у записи: только когда упали элементы, а не значения готового слова.
  const partialOf = signalState === 'failed' && summary.status === 'partial' && counts.failed > 0 && counts.total > 0;
  return {
    id: entry.id,
    state: signalState,
    kind: summary.kind,
    title: summary.title,
    cards: single && (signalState === 'generating' || signalState === 'ready') && counts.total > 0 ? counts.total : null,
    failedOf: single && partialOf ? { failed: counts.failed, total: counts.total } : null,
    more: candidates.length - 1,
    count: candidates.length,
    dismissible: signalState === 'failed',
    suppressed: !overlayReady(state, now) || state.keyboardOpen || input.blocked,
  };
}

/**
 * Шаг модели сигнала (зовёт строка создания раз в 100 мс, пока есть нажатия этого захода, и на каждое
 * изменение): решение о сигнале запоминается, итог законченной генерации принимается (ТЗ § 8, «Принятие»):
 * 1) её строка была на экране не меньше 1 с подряд после конца, вкладка видна, нет затемнения и клавиатуры;
 * 2) «Готово» пробыло на виду 6 с — отсчёт идёт, пока сигнал показан и вкладка видна, и стоит под курсором
 *    и фокусом. Третий путь — нажатие сигнала или ✕ (`pressSignal`, `dismissWatched`).
 * Ничего не изменилось — прежний объект: стор не будит подписчиков.
 */
export function stepSignal(state: WatchState, summaryOf: SummaryOf, input: SignalInput, now: number): WatchState {
  if (!state.homeVisible) return state;
  const view = computeSignal(state, summaryOf, input, now);
  const readyShown =
    view && !view.suppressed && input.tabVisible && !input.held && (view.state === 'ready' || view.state === 'added')
      ? view.id
      : null;
  const calm = input.tabVisible && !state.overlayOpen && !state.keyboardOpen;
  let entries: Record<string, WatchEntry> | null = null;
  for (const entry of Object.values(state.entries)) {
    if (entry.visit !== state.visit || entry.seenFinal || entry.dismissed) continue;
    const summary = summaryOf(entry.id);
    if (!summary) continue;
    let next = entry;
    if (next.decidedAt === null && isSignalDecided(state, next, now)) next = { ...next, decidedAt: now };
    if (isFinished(summary)) {
      // 1. Строка на экране 1 с подряд после конца генерации.
      if (calm && isRowInView(state, entry.id, now)) {
        const since = next.seenSince ?? now;
        if (next.seenSince === null) next = { ...next, seenSince: now };
        if (now - since >= ACCEPT_IN_VIEW_MS) next = { ...next, seenFinal: true };
      } else if (next.seenSince !== null) {
        next = { ...next, seenSince: null };
      }
      // 2. «Готово» на виду 6 с (с паузами).
      if (readyShown === entry.id && !next.seenFinal) {
        const since = next.readyShownSince ?? now;
        if (next.readyShownSince === null) next = { ...next, readyShownSince: now };
        const shown = next.readyShownMs + (now - since);
        if (shown >= READY_SHOWN_MS) next = { ...next, readyShownMs: shown, readyShownSince: null, seenFinal: true };
      } else if (next.readyShownSince !== null) {
        next = { ...next, readyShownMs: next.readyShownMs + (now - next.readyShownSince), readyShownSince: null };
      }
    } else if (next.seenSince !== null) {
      // Генерация снова идёт («Повторить») или все карточки удалены — принятие начинается заново.
      next = { ...next, seenSince: null };
    }
    if (next !== entry) {
      entries = entries ?? { ...state.entries };
      entries[entry.id] = next;
    }
  }
  return entries ? { ...state, entries } : state;
}

/**
 * Нажали сигнал (ТЗ § 8, «Нажатие»): список раскрывает запись сигнала и прокручивает к ней. Законченная
 * генерация принята; идущая просто не показывается в сигнале, пока едет прокрутка, — ушла запись с экрана,
 * пока генерация идёт, — сигнал вернётся.
 */
export function pressSignal(state: WatchState, id: string, summary: GenerationSummary | null, now: number): WatchState {
  const next = requestReveal(state, id, now);
  const entry = state.entries[id];
  if (!entry) return next;
  const patch: Partial<WatchEntry> = summary && isFinished(summary) ? { seenFinal: true } : { revealedAt: now };
  return { ...next, entries: { ...next.entries, [id]: { ...entry, ...patch } } };
}

/** Есть нажатия этого захода, по которым сигнал ещё может появиться, смениться или уйти: нужен шаг модели. */
export function hasSignalWork(state: WatchState, summaryOf: SummaryOf): boolean {
  if (!state.homeVisible) return false;
  return Object.values(state.entries).some(
    (entry) =>
      entry.visit === state.visit && !entry.seenFinal && !entry.dismissed && summaryOf(entry.id)?.status !== 'removed',
  );
}

/** Шаг модели, пока что-то решается по времени: 300 мс, 150 мс у границы, 1 с, 6 с, удержание после нажатия. */
export const SIGNAL_FAST_TICK_MS = 100;
/** Шаг модели, когда по времени решать нечего (сигнал стоит, «дольше обычного» — через минуты). */
export const SIGNAL_IDLE_TICK_MS = 1000;

/**
 * Через сколько сделать следующий шаг модели сигнала: `null` — работы нет; часто — пока что-то решается
 * по времени; редко — когда сигнал просто стоит (изменения слежения и так запускают шаг сразу).
 */
export function signalTickMs(state: WatchState, summaryOf: SummaryOf, now: number): number | null {
  if (!hasSignalWork(state, summaryOf)) return null;
  if (now < state.overlayFreeAt) return SIGNAL_FAST_TICK_MS;
  if (Object.values(state.inView).some((view) => now - view.at < VIEW_SETTLE_MS)) return SIGNAL_FAST_TICK_MS;
  const hot = Object.values(state.entries).some(
    (entry) =>
      entry.visit === state.visit &&
      !entry.seenFinal &&
      !entry.dismissed &&
      (entry.decidedAt === null ||
        entry.seenSince !== null ||
        entry.readyShownSince !== null ||
        (entry.revealedAt !== null && now - entry.revealedAt < REVEAL_HOLD_MS)),
  );
  return hot ? SIGNAL_FAST_TICK_MS : SIGNAL_IDLE_TICK_MS;
}

/** Объявление скрытой live-области; `summary: null` — отметить молча (удалено, тост уже показан). */
export interface SpeechItem {
  id: string;
  phase: 'started' | 'final';
  summary: GenerationSummary | null;
}

/**
 * Что объявить экранному диктору (ТЗ § 8): «Генерируем travel» — по нажатию этого захода, итог — по
 * любой отслеживаемой генерации, виден сигнал или нет; каждое — один раз. Итог молчит, если тост о нём уже
 * показан (ушёл с главной до конца), если все карточки удалены или если начала этот заход не слышал.
 */
export function pendingSpeech(state: WatchState, summaryOf: SummaryOf): SpeechItem[] {
  if (!state.homeVisible) return [];
  const out: SpeechItem[] = [];
  for (const entry of Object.values(state.entries)) {
    if (entry.spoken === 'final') continue;
    const summary = summaryOf(entry.id);
    if (!summary) continue;
    const current = entry.visit === state.visit;
    if (isActiveSummary(summary)) {
      if (current && entry.spoken === 'none') out.push({ id: entry.id, phase: 'started', summary });
      continue;
    }
    const silent = summary.status === 'removed' || entry.announced || (!current && entry.spoken === 'none');
    out.push({ id: entry.id, phase: 'final', summary: silent ? null : summary });
  }
  return out;
}

export function markSpoken(state: WatchState, items: readonly Pick<SpeechItem, 'id' | 'phase'>[]): WatchState {
  let entries: Record<string, WatchEntry> | null = null;
  for (const item of items) {
    const entry = (entries ?? state.entries)[item.id];
    if (!entry || entry.spoken === item.phase) continue;
    entries = entries ?? { ...state.entries };
    entries[item.id] = { ...entry, spoken: item.phase };
  }
  return entries ? { ...state, entries } : state;
}
